import {UseFormReturn} from 'react-hook-form';

import {isNumber} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {FormValuesType} from '../types/FormValuesType';
import {HUNDRED_PERCENT} from './getPercentage';

const DEFAULT_PRICE = '0';

interface SyncAmountAndPercentageArgs {
  val: string | number | null;
  formApi: UseFormReturn<FormValuesType>;
  initiator: 'amount' | 'percentage';
  orderPrice: string;
  updateExchangeRate: (formApi: UseFormReturn<FormValuesType>) => void;
}

/**
 * Keeps a partial payment's amount and percentage fields in sync, then re-derives the foreign-
 * currency exchange rate for whatever the outcome was — including the empty/zero early return —
 * so that path never leaves a stale unsent-edit guard behind (T20-138847).
 */
export const syncAmountAndPercentage = ({
  val,
  formApi,
  initiator,
  orderPrice,
  updateExchangeRate,
}: SyncAmountAndPercentageArgs) => {
  const orderPriceNumber = Number(orderPrice);
  const maxPrice = initiator === 'amount' ? orderPriceNumber : HUNDRED_PERCENT;
  const updateInput = initiator === 'amount' ? 'percentage' : 'amount';

  if (!isNumber(val) || val === 0) {
    formApi.setValue(updateInput, 0);
    formApi.setValue('exchangeRateAmount', DEFAULT_PRICE);
    updateExchangeRate(formApi);
    return;
  }

  if (val > maxPrice) {
    formApi.setError(initiator, {
      message: i18n.t('general.errors.number.max', {max: maxPrice}),
    });
    return;
  }

  const percentage =
    initiator === 'amount'
      ? (val / orderPriceNumber) * HUNDRED_PERCENT
      : (val / HUNDRED_PERCENT) * orderPriceNumber;

  formApi.setValue(updateInput, Number(percentage.toFixed(2)));

  formApi.clearErrors(['amount', 'percentage']);
  updateExchangeRate(formApi);
};
